import {EllipsisIcon} from '@primer/octicons-react'

import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import type {SetStickyLinesType} from '../../../../hooks/use-sticky-lines'
import {
  determineProperOffsetWithDummyDiv,
  expandRow,
  getActualLineNumberBinarySearch,
  useCollapsedLinesSubscription,
  useIsLineCollapsed,
} from '../../../../utilities/lines'
import styles from './CodeFoldingEllipsisOverlay.module.css'
import {HighlighterElement} from './CodeLine'
import type {CodeLineData} from './hooks/use-code-lines'

export function CodeFoldingEllipsisOverlay({
  linesData,
  onLineStickOrUnstick,
  setIsCollapsed,
  tabSize,
  extraLeftPadding,
  contentWidth,
}: {
  linesData: CodeLineData[]
  tabSize: number
  contentWidth?: number
  onLineStickOrUnstick?: SetStickyLinesType
  setIsCollapsed?: () => void
  extraLeftPadding?: number
}) {
  const collapsedRows = useCollapsedLinesSubscription()
  const collapsedRowsArray = [...collapsedRows.keys()]
  const lineHeight = useCurrentLineHeight('react-line-numbers')

  return (
    <>
      {collapsedRowsArray.map(lineNumber => {
        const actualLineNumber = getActualLineNumberBinarySearch(lineNumber, linesData)
        if (actualLineNumber === undefined || linesData[actualLineNumber]?.ownedSection === undefined) return null
        const lineData = linesData[actualLineNumber]

        const leftOffset = determineProperOffsetWithDummyDiv(
          lineData?.rawText?.length ?? 0,
          lineData?.rawText ?? '',
          tabSize,
        )
        return (
          <div
            key={`expand-row-ellipsis-${lineNumber}`}
            className={styles.ellipsisRow}
            style={{top: lineHeight * actualLineNumber, height: lineHeight}}
          >
            {contentWidth ? (
              <HighlighterElement
                subtle
                lineNumber={lineNumber}
                highlightPosition={{offset: -82, width: contentWidth}}
              />
            ) : null}
            <ExpandRowEllipsis
              codeLineData={lineData}
              setIsCollapsed={setIsCollapsed}
              onLineStickOrUnstick={onLineStickOrUnstick}
              leftOffset={leftOffset + (extraLeftPadding ?? 0)}
            />
          </div>
        )
      })}
    </>
  )
}

interface ExpandRowEllipsisProps {
  codeLineData: CodeLineData
  leftOffset: number
  setIsCollapsed?: (isCollapsed: boolean) => void
  onLineStickOrUnstick?: SetStickyLinesType
}

export function ExpandRowEllipsis({
  codeLineData,
  leftOffset,
  setIsCollapsed,
  onLineStickOrUnstick,
}: ExpandRowEllipsisProps) {
  const {lineNumber, ownedSection} = codeLineData
  const collapsed = useIsLineCollapsed(lineNumber)

  if (!collapsed) return null

  return (
    <button
      type="button"
      aria-label="Expand row"
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className="Button Button--iconOnly Button--invisible Button--small px-2 py-0 ml-1 border-0 expand-row-ellipsis"
      style={{left: leftOffset}}
      onMouseDown={ev => {
        ev.preventDefault()
      }}
      onClick={event => {
        expandRow(lineNumber)
        setIsCollapsed?.(false)

        if (ownedSection) {
          onLineStickOrUnstick?.(codeLineData, true)
        }

        // When the click event was triggered by a keyboard event, we want to focus the chevron after the row expands
        if (event.detail === 0) {
          requestAnimationFrame(() => {
            document
              .querySelector<HTMLElement>(`[data-line-number="${lineNumber}"] [data-code-folding-chevron]`)
              ?.focus({preventScroll: true})
          })
        }
      }}
    >
      <EllipsisIcon />
    </button>
  )
}
